"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mergeRoles = mergeRoles;
exports.scopeFor = scopeFor;
exports.canAtAll = canAtAll;
exports.fieldAccess = fieldAccess;
exports.hiddenFields = hiddenFields;
exports.readOnlyFields = readOnlyFields;
const catalogue_1 = require("./catalogue");
/**
 * Merge every role the member holds. **Most permissive wins**, per CLAUDE.md.
 *
 * ⚠️ **A member with NO roles gets an empty set, which denies everything.** That is the right
 * answer and it is worth a sentence: the tempting alternative — treating "no roles" as "not
 * configured yet, so allow" — is how a half-provisioned member becomes an administrator.
 */
function mergeRoles(roles) {
    const grants = {};
    const fieldRules = {};
    for (const role of roles) {
        for (const [action, scope] of Object.entries(role.grants)) {
            const current = grants[action];
            grants[action] = current === undefined ? scope : (0, catalogue_1.widerScope)(current, scope);
        }
        for (const [field, access] of Object.entries(role.fieldRules ?? {})) {
            const current = fieldRules[field];
            fieldRules[field] = current === undefined ? access : (0, catalogue_1.widerFieldAccess)(current, access);
        }
    }
    return { grants, fieldRules };
}
/**
 * The scope this member holds for one action. `none` when they hold nothing.
 *
 * ⚠️ **Returns a SCOPE, not a boolean, and callers must not reduce it to one carelessly.**
 * `scopeFor(...) !== 'none'` answers "may they do this at all"; it does not answer "may they do
 * it to *this row*". A service that checks the first and forgets the second lets somebody with
 * `own` scope edit anybody's worklog — which is `worklog.edit.any` granted by accident, the exact
 * distinction the mockup's key name was trying to make.
 */
function scopeFor(permissions, action) {
    return permissions.grants[action] ?? 'none';
}
/** Whether the member may perform the action at all, at any scope. */
function canAtAll(permissions, action) {
    return scopeFor(permissions, action) !== 'none';
}
/**
 * What the member may do to one field.
 *
 * ⚠️ **An unlisted field is `write`, which is the opposite of the action default, and the
 * asymmetry is deliberate.** A field rule is a RESTRICTION on a resource the member already has
 * permission to update — they got through `canAtAll` to be here. Defaulting to `hidden` would
 * mean every new column is invisible to everybody until somebody enumerates it in every role,
 * and the failure mode is a blank screen with no error. Defaulting to `write` means a new column
 * is visible until somebody restricts it, and the failure mode is a field that should have been
 * hidden — which a reviewer can see.
 *
 * The safe default is at the ACTION level, where it belongs: no action, no access to the resource
 * at all.
 */
function fieldAccess(permissions, resource, field) {
    return permissions.fieldRules[`${resource}.${field}`] ?? 'write';
}
/** Fields the member must not see at all. Serialisation drops these; it does not blank them. */
function hiddenFields(permissions, resource) {
    const prefix = `${resource}.`;
    return Object.entries(permissions.fieldRules)
        .filter(([key, access]) => key.startsWith(prefix) && access === 'hidden')
        .map(([key]) => key.slice(prefix.length));
}
/**
 * Fields the member may read but not write.
 *
 * ⚠️ **Request validation needs this, not just serialisation.** CLAUDE.md: field rules are
 * enforced *centrally — in serialization and in request validation — never per endpoint*. A
 * read-only field that is only enforced on the way out is a field anybody can still write.
 */
function readOnlyFields(permissions, resource) {
    const prefix = `${resource}.`;
    return Object.entries(permissions.fieldRules)
        .filter(([key, access]) => key.startsWith(prefix) && access === 'read')
        .map(([key]) => key.slice(prefix.length));
}
//# sourceMappingURL=effective.js.map